<template>
  <div :class="`${createRenderType}-trending-main`" class="trending-main">
    <div v-if="!loading">
      <div class="flex-space-between head">
        <div class="title">
          {{ $$t("credit.trendingSongs") }}
        </div>
        <div v-if="createRenderType == 'pc'" class="change-btn flex">
          <div
            class="btn-item flex-center"
            :class="showData.first ? 'btn-item-disabled' : 'pointer'"
            @click="changePrev"
          >
            <img src="@/assets/img/content/prev.png" alt="" />
          </div>
          <div
            class="btn-item flex-center"
            :class="showData.last ? 'btn-item-disabled' : 'pointer'"
            @click="changeNext"
          >
            <img src="@/assets/img/content/next.png" alt="" />
          </div>
        </div>
      </div>
      <div class="trend-list" ref="refTrendSwiperBox">
        <div class="swiper" ref="swiperContainer">
          <div class="swiper-wrapper">
            <div
              class="swiper-slide"
              v-for="(item, index) in trendingSongList"
              :key="index"
            >
              <TrendingCard
                :data="item"
                @changeFavorite="(state) => changeFavorite(item, state)"
                @handleShare="handleShare(item)"
                @handleMore="handleMore(item)"
                @togglePlay="togglePlay(item)"
                @onCurPlayEnded="onCurPlayEnded(item)"
                @handleSimilar="onHandleSimilar"
              ></TrendingCard>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-if="loading" :class="`${createRenderType}-trending-skeleton`">
      <el-skeleton
        :rows="5"
        :style="{
          '--el-fill-color': 'rgb(255, 255, 255, 0.1)',
        }"
      />
    </div>
  </div>
</template>

<script setup>
import {
  ref,
  defineProps,
  nextTick,
  defineEmits,
  getCurrentInstance,
  onMounted,
} from "vue";
import SwiperCore from "swiper";
import "swiper/css";
import useCreateLayoutStore from "@/store/create/createLayoutStore.js";
import useDiscoverStore from "@/store/discover/discoverDataStore.js";
import useUserDataStore from "@/store/userDataStore";
import TrendingCard from "./trendingCard.vue";

const globalProperties =
  getCurrentInstance()?.appContext.config.globalProperties;
const { $$t } = globalProperties?.$i18n;
const createLayoutStore = useCreateLayoutStore();
const { createRenderType } = storeToRefs(createLayoutStore);
const discoverDataStore = useDiscoverStore();
const { trendingSongList } = storeToRefs(discoverDataStore);
const userDataStore = useUserDataStore();

const emits = defineEmits([
  "changeFavorite",
  "togglePlay",
  "onCurPlayEnded",
  "handleShare",
  "handleSimilar",
]);
const props = defineProps({
  loading: {
    type: Boolean,
    default: false,
  },
});
const swiperInstance = ref(null);
const swiperContainer = ref(null);
const refTrendSwiperBox = ref(null);
const observerObj = {
  first: null,
  last: null,
};
const showData = ref({
  first: false,
  last: false,
});
const swiperActiveIndex = ref(0);

const changeFavorite = (item, state) => {
  emits("changeFavorite", item, state);
};

const handleMore = (item) => {
  emits("handleMore", item);
};

const togglePlay = (item) => {
  emits("togglePlay", item);
};

const onCurPlayEnded = (item) => {
  emits("onCurPlayEnded", item);
};

const handleShare = (item) => {
  emits("handleShare", item);
};

const onHandleSimilar = (item) => {
  emits("handleSimilar", item);
};

const initEvent = () => {
  nextTick(() => {
    if (trendingSongList.value.length) {
      setTimeout(() => {
        let els = refTrendSwiperBox.value
          ?.querySelector(".swiper-wrapper")
          ?.querySelectorAll(".swiper-slide");

        if (els && els.length > 1) {
          let elf = els[0];
          let elr = els[trendingSongList.value.length - 1];
          isElementHalfVisible(elf, "first");
          isElementHalfVisible(elr, "last");
        }
      }, 20);
    }
  });
};

const isElementHalfVisible = (element, type) => {
  observerObj[type] = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (type === "first") {
          // 第一个元素完全可见时才禁用"上一个"按钮
          showData.value.first =
            entry.isIntersecting && entry.intersectionRatio >= 0.95;
        } else {
          // 最后一个元素完全可见时才禁用"下一个"按钮
          showData.value.last =
            entry.isIntersecting && entry.intersectionRatio >= 0.95;
        }
      });
    },
    {
      root: refTrendSwiperBox.value,
      threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1.0],
    }
  );

  observerObj[type].observe(element);
};

const onSlideChange = (swiper) => {
  swiperActiveIndex.value = swiper.activeIndex;
};

const changePrev = () => {
  if (showData.value.first) return;
  swiperInstance.value?.slideTo(swiperActiveIndex.value - 3);
};

const changeNext = () => {
  if (showData.value.last) return;
  swiperInstance.value?.slideTo(swiperActiveIndex.value + 3);
};

const initSwiper = () => {
  if (swiperInstance.value) {
    swiperInstance.value.destroy(true, true);
    swiperInstance.value = null;
  }
  swiperInstance.value = new SwiperCore(swiperContainer.value, {
    slidesPerView: "auto",
    spaceBetween: 20,
    simulateTouch: true,
    allowTouchMove: true,
    grabCursor: true,
    on: {
      slideChange: onSlideChange,
    },
  });
};

watch(
  () => props.loading,
  (newVal) => {
    if (!newVal) {
      nextTick(() => {
        initSwiper();
        initEvent();
      });
    }
  }
);
</script>

<style lang="scss">
@keyframes opacityAnimation {
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0.4;
  }
  100% {
    opacity: 1;
  }
}

.pc-trending-skeleton {
  padding-right: 20px;
  animation: opacityAnimation 1.8s infinite;
}

.mobile-trending-skeleton {
  padding: 0 16px;
  animation: opacityAnimation 1.8s infinite;
}
</style>

<style scoped>
.swiper {
  width: 100%;
  overflow: hidden;
  cursor: grab;
}
.swiper:active {
  cursor: grabbing;
}
.swiper-wrapper {
  display: flex;
}
.swiper-slide {
  flex-shrink: 0;
  /* width: 200px;
  height: 120px; */
  width: max-content;
  height: max-content;
  /* background: #ccc; */
  margin-right: 10px;
}
</style>

<!-- <style lang="scss">
.trending-main {
  .swiper-slide {
    width: max-content !important;
  }
  .swiper-wrapper {
    display: flex;
  }
}

@keyframes opacityAnimation {
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0.4;
  }
  100% {
    opacity: 1;
  }
}

.pc-trending-skeleton {
  padding-right: 20px;
  animation: opacityAnimation 1.8s infinite;
}

.mobile-trending-skeleton {
  padding: 0 16px;
  animation: opacityAnimation 1.8s infinite;
}
</style> -->

<style lang="scss" scoped>
.trending-main {
  .head {
    padding-right: 20px;
    .title {
      font-weight: bold;
    }
    .change-btn {
      .btn-item {
        width: 40px;
        height: 40px;
        background: rgba(255, 255, 255, 0.1);
        border-radius: 50%;
        margin-left: 12px;
        &-disabled {
          opacity: 0.3;
        }
        img {
          width: 20px;
          height: 20px;
        }
      }
      .pointer {
        &:hover {
          background: rgba(255, 255, 255, 0.2);
        }
      }
    }
  }
}

.pc-trending-main {
  .trend-list {
    height: 300px;
    padding-bottom: 48px;
    height: max-content;
    margin-top: 16px;
    overflow-x: hidden;
  }
  .head {
    .title {
      font-size: 32px;
      line-height: 42px;
    }
  }
  .trend-list {
    margin-top: 24px;
  }
  .pc-trending-skeleton {
  }
}

.mobile-trending-main {
  padding-top: 14px;
  .trend-list {
    padding-bottom: 16px;
    height: max-content;
    margin-top: 16px;
    overflow-x: hidden;
  }
  .head {
    padding-left: 16px;
    .title {
      font-size: 24px;
      line-height: 32px;
    }
  }
  .trend-list {
    padding-left: 16px;
  }
}
</style>
